import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Badge, IconButton, Menu, Pill, SearchField } from '../ui';
import { CloseIcon, LogoutIcon, MailIcon, PlusIcon, SettingsIcon, ShieldIcon } from '../ui/icons';
import { collectionIcon, initials, ownedCollections, sharedCollections } from './collections';
import SavedViewsNav from './SavedViewsNav';

export function Logo({ onClick }) {
  return (
    <Link to="/" className="shell-logo" onClick={onClick}>
      <span className="shell-logo__mark" aria-hidden="true">I</span>
      <span className="shell-logo__word">Inventorium</span>
    </Link>
  );
}

/** "1 112 objets" read out, "1 112" shown. */
export function ItemCount({ count, lang }) {
  const { t } = useTranslation();
  if (count == null) return null;
  return (
    <span className="shell-nav__count">
      <span aria-hidden="true">{new Intl.NumberFormat(lang).format(count)}</span>
      <span className="visually-hidden">{t('shell.itemCount', { count })}</span>
    </span>
  );
}

function CollectionLink({ inventory, count, lang, shared, onNavigate, current }) {
  const { t } = useTranslation();
  const Icon = collectionIcon(inventory);
  return (
    <li>
      <NavLink to={`/c/${inventory.id}`} className="shell-nav__link" onClick={onNavigate}>
        <Icon className="shell-nav__icon" />
        {shared ? (
          <span className="shell-nav__label shell-nav__label--stack">
            <span className="shell-nav__name">{inventory.name}</span>
            <span className="shell-nav__meta">{t('shell.byOwner', { name: inventory.owner_name })}</span>
          </span>
        ) : (
          <span className="shell-nav__label"><span className="shell-nav__name">{inventory.name}</span></span>
        )}
        {shared ? (
          <Pill tone={inventory.share_permission === 'edit' ? 'accent' : 'amber'}>
            <span aria-hidden="true">{t(inventory.share_permission === 'edit' ? 'shell.permEdit' : 'shell.permRead')}</span>
            <span className="visually-hidden">{t(inventory.share_permission === 'edit' ? 'shell.permEditLong' : 'shell.permReadLong')}</span>
          </Pill>
        ) : (
          <ItemCount count={count} lang={lang} />
        )}
      </NavLink>
      {current && <SavedViewsNav inventory={inventory} onNavigate={onNavigate} />}
    </li>
  );
}

/**
 * The sidebar: logo, search, my collections (the open one with its saved
 * views), shared with me, settings, administration, account. Static on desktop, a drawer below 1024 px.
 */
function Sidebar({
  user, inventories, countOf, pendingCount, search, onNewCollection, onInvite, onLogout, onNavigate,
  onCloseDrawer, drawer, section, collectionId,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || 'en';
  const isAdmin = user.role === 'admin';
  const owned = ownedCollections(inventories);
  const shared = sharedCollections(inventories);

  return (
    <div className="shell-sidebar__inner">
      <div className="shell-sidebar__top">
        <Logo onClick={onNavigate} />
        {drawer && (
          <IconButton icon={CloseIcon} label={t('shell.closeMenu')} variant="ghost" onClick={onCloseDrawer} className="shell-sidebar__close" />
        )}
      </div>

      <form role="search" className="shell-search" onSubmit={(e) => { e.preventDefault(); search.onSubmit(); }}>
        <SearchField
          ref={search.inputRef}
          quiet
          label={search.label}
          placeholder={search.placeholder}
          value={search.value}
          onChange={search.onChange}
          shortcut="/"
          aria-keyshortcuts="/"
        />
      </form>

      <nav aria-label={t('shell.mainNav')} className="shell-nav">
        <section className="shell-nav__section" aria-labelledby="shell-mine">
          <h2 id="shell-mine" className="shell-nav__heading">
            <Link to="/" onClick={onNavigate}>{t('shell.myCollections')}</Link>
          </h2>
          <ul className="shell-nav__list">
            {owned.map((inv) => (
              <CollectionLink key={inv.id} inventory={inv} count={countOf(inv)} lang={lang} onNavigate={onNavigate}
                              current={String(inv.id) === collectionId} />
            ))}
            {owned.length === 0 && <li className="shell-nav__empty">{t('shell.noCollections')}</li>}
            <li>
              <button type="button" className="shell-nav__link shell-nav__link--action" onClick={onNewCollection}>
                <PlusIcon className="shell-nav__icon" />
                <span className="shell-nav__label">{t('shell.newCollection')}</span>
              </button>
            </li>
          </ul>
        </section>

        {shared.length > 0 && (
          <section className="shell-nav__section" aria-labelledby="shell-shared">
            <h2 id="shell-shared" className="shell-nav__heading">{t('shell.sharedWithMe')}</h2>
            <ul className="shell-nav__list">
              {shared.map((inv) => (
                <CollectionLink key={inv.id} inventory={inv} lang={lang} shared onNavigate={onNavigate}
                              current={String(inv.id) === collectionId} />
              ))}
            </ul>
          </section>
        )}
      </nav>

      <div className="shell-sidebar__bottom">
        <ul className="shell-nav__list" aria-label={t('shell.otherPages')}>
          <li>
            <Link to="/settings" className={`shell-nav__link${section === 'settings' ? ' active' : ''}`}
                  aria-current={section === 'settings' ? 'page' : undefined} onClick={onNavigate}>
              <SettingsIcon className="shell-nav__icon" />
              <span className="shell-nav__label">{t('shell.settings')}</span>
            </Link>
          </li>
          {isAdmin && (
            <li>
              <Link to="/admin/accounts" className={`shell-nav__link${section === 'admin' ? ' active' : ''}`}
                    aria-current={section === 'admin' ? 'page' : undefined} onClick={onNavigate}>
                <ShieldIcon className="shell-nav__icon" />
                <span className="shell-nav__label">{t('shell.administration')}</span>
                {pendingCount > 0 && (
                  <Badge tone="accent" count>
                    <span aria-hidden="true">{pendingCount}</span>
                    <span className="visually-hidden">{t('shell.pendingAccounts', { count: pendingCount })}</span>
                  </Badge>
                )}
              </Link>
            </li>
          )}
        </ul>

        <div className="shell-account">
          <Menu
            label={t('shell.accountMenu')}
            placement="top"
            className="shell-account__menu"
            items={[
              { id: 'invite', label: t('nav.invite'), icon: MailIcon, onSelect: onInvite },
              'separator',
              { id: 'logout', label: t('nav.logout'), icon: LogoutIcon, onSelect: onLogout },
            ]}
            trigger={(props) => (
              <button type="button" {...props} className="shell-account__button">
                <span className="shell-account__avatar" aria-hidden="true">{initials(user.display_name || user.email)}</span>
                <span className="shell-account__who">
                  <span className="shell-account__name">{user.display_name}</span>
                  <span className="shell-account__role">{t(isAdmin ? 'shell.roleAdmin' : 'shell.roleUser')}</span>
                </span>
                <span className="visually-hidden">{t('shell.accountMenu')}</span>
              </button>
            )}
          />
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
